/**
 * DensityControl — a two-way Comfortable / Compact segmented toggle that folds
 * the old Pitch mode (uniform/variable) + Tight-pack row knobs into one choice.
 * Controlled + presentational; callers map the `Density` to their own fields via
 * the helpers in `lib/placement/formations`.
 */
import { DENSITY_LABELS, type Density } from '@/lib/placement/formations'
import { cn } from '@/lib/utils'

type Tone = 'default' | 'dark'

interface Props {
  value: Density
  onChange: (d: Density) => void
  tone?: Tone
  disabled?: boolean
}

const ORDER: Density[] = ['comfortable', 'compact']

const TONES: Record<Tone, { group: string; seg: string; active: string }> = {
  default: {
    group: 'border-border bg-muted',
    seg: 'text-muted-foreground hover:text-foreground',
    active: 'bg-background text-foreground shadow-sm',
  },
  dark: {
    group: 'border-white/10 bg-white/5',
    seg: 'text-white/50 hover:text-white/80',
    active: 'bg-sky-500/20 text-sky-100',
  },
}

export function DensityControl({ value, onChange, tone = 'default', disabled }: Props) {
  const t = TONES[tone]
  return (
    <div
      className={cn('inline-flex w-full rounded-md border p-0.5', t.group)}
      role="group"
      aria-label="Density"
    >
      {ORDER.map((d) => {
        const active = value === d
        return (
          <button
            key={d}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(d)}
            className={cn(
              'flex-1 rounded px-2 py-1 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              active ? t.active : t.seg
            )}
          >
            {DENSITY_LABELS[d]}
          </button>
        )
      })}
    </div>
  )
}
